import { PsychologistProfile } from "core/types";
import { forwardRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { Icon, TextField } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { useDemo } from "contexts/DemoContext";
import { useApplicationMarket } from "../../../core/selectors/marketSelectors";
import { HeaderRow, InfoButton } from "../styled";
import { getComments, getUpdatedComment } from "./commentFormatting";
import { CommentInfoModal } from "./commentInfoModal";
import type { CreatePsychologistComments } from "./type";

type Props = {
  psychologist: Pick<PsychologistProfile, "comments">;
  onChangeComments: (comments: CreatePsychologistComments) => void;
  psychologistValidation: string[];
};

const Comment = forwardRef<HTMLDivElement, Props>(
  ({ psychologist, onChangeComments, psychologistValidation }, ref) => {
    const { t } = useTranslation();
    const activeCountry = useApplicationMarket();
    const demo = useDemo();

    const [isInfoOpen, setInfoOpen] = useState(false);

    const { defaultLanguageId, languages: availableLanguages } = activeCountry;

    const comments = getComments({
      comments: psychologist.comments,
      activeCountry: activeCountry,
      defaultLanguageId,
    });

    return (
      <div ref={ref}>
        <CommentInfoModal
          open={isInfoOpen}
          onClose={() => setInfoOpen(false)}
        />

        <HeaderRow>
          <Typography variant="h3" style={{ margin: "10px 0px" }} insensitive>
            {t("PSYCHOLOGIST_COMMENT_TITLE")}
          </Typography>
          <InfoButton onClick={() => setInfoOpen(true)}>
            <Icon.Info />
          </InfoButton>
        </HeaderRow>
        <Typography
          variant="body-small"
          style={{ margin: "10px 0px" }}
          insensitive
        >
          {t("COMMENT_INFO")}
        </Typography>
        <CommentsContainer>
          {comments?.map((comment, index) => {
            const isDefaultForCountry =
              comment.countryLanguage.languageId === defaultLanguageId;
            return (
              <TextField
                key={`comment_lang_${index}`}
                hideSensitiveData={false}
                label={`${t(
                  availableLanguages
                    ?.find(
                      (language) =>
                        language.languageId ===
                        comment.countryLanguage.languageId,
                    )
                    ?.codeISO3.toUpperCase() || "",
                )}`}
                placeholder={
                  isDefaultForCountry
                    ? t("ENTER_DESCRIPTION")
                    : t("ENTER_TRANSLATION")
                }
                multiline={true}
                value={comment.comment}
                sensitiveField={demo}
                onChange={(e) => {
                  const updatedComments = getUpdatedComment(
                    comments,
                    comment.countryLanguage.countryId,
                    comment.countryLanguage.languageId,
                    e.target.value,
                  );
                  onChangeComments(updatedComments);
                }}
                maxlength={150}
                error={psychologistValidation.includes("comments")}
                errorMessage={t("COMMENT_TOO_SHORT_ERROR")}
              />
            );
          })}
        </CommentsContainer>
      </div>
    );
  },
);

const CommentsContainer = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

Comment.displayName = "Comment";

export default Comment;
